import { canExitLesson, getInteractiveBlockIds } from "@/lib/ile/progress";
import type { LessonContent } from "@/lib/lesson-content/schema";

/** Bump when storage shape or invalidation rules change. */
export const ILE_STORAGE_VERSION = 4;

/** Per-block interaction result, kept so revisits can show what was attempted. */
export type IleBlockResult = {
  correct?: boolean;
  attempts?: number;
  /** Hints taken on a hinted block; reported so the server can reduce the award. */
  hintsUsed?: number;
  /** The learner's first (wrong) answer, serialized per block type, for reinforcement. */
  firstWrong?: unknown;
  /** The learner's submitted answer, validated server-side when crediting points. */
  answer?: unknown;
};

function storageUserScope(userId?: string | null): string {
  return userId && userId.trim() ? `${userId.trim()}:` : "anon:";
}

export function ileProgressStorageKey(lessonId: string, userId?: string | null): string {
  return `ile-v${ILE_STORAGE_VERSION}-progress:${storageUserScope(userId)}${lessonId}`;
}

export function ileSectionStorageKey(lessonId: string, userId?: string | null): string {
  return `ile-v${ILE_STORAGE_VERSION}-section:${storageUserScope(userId)}${lessonId}`;
}

export function ileResultsStorageKey(lessonId: string, userId?: string | null): string {
  return `ile-v${ILE_STORAGE_VERSION}-results:${storageUserScope(userId)}${lessonId}`;
}

export function ileMetaStorageKey(lessonId: string, userId?: string | null): string {
  return `ile-v${ILE_STORAGE_VERSION}-meta:${storageUserScope(userId)}${lessonId}`;
}

/** Fingerprint of section/block ids — invalidates session when content is re-seeded or restructured. */
export function ileContentFingerprint(content: LessonContent): string {
  const sectionIds = content.sections.map((s) => s.id).join("|");
  const blockIds = content.sections.flatMap((s) => s.blocks.map((b) => b.id)).join("|");
  return `${content.sections.length}:${sectionIds}::${blockIds}`;
}

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage ?? window.sessionStorage ?? null;
  } catch {
    try {
      return window.sessionStorage ?? null;
    } catch {
      return null;
    }
  }
}

export function clearIleSessionProgress(lessonId: string, userId?: string | null): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(ileProgressStorageKey(lessonId, userId));
    storage.removeItem(ileSectionStorageKey(lessonId, userId));
    storage.removeItem(ileResultsStorageKey(lessonId, userId));
    storage.removeItem(ileMetaStorageKey(lessonId, userId));
    // Legacy keys (pre-sections / v1 / v3)
    storage.removeItem(`ile-progress:${lessonId}`);
    storage.removeItem(`ile-section:${lessonId}`);
    storage.removeItem(`ile-v3-progress:${lessonId}`);
    storage.removeItem(`ile-v3-section:${lessonId}`);
    storage.removeItem(`ile-v3-results:${lessonId}`);
    storage.removeItem(`ile-v3-meta:${lessonId}`);
  } catch {
    // ignore
  }
}

export function saveIleSessionProgress(
  lessonId: string,
  content: LessonContent,
  completedBlockIds: ReadonlySet<string>,
  sectionIndex: number,
  blockResults?: ReadonlyMap<string, IleBlockResult>,
  userId?: string | null,
): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(ileMetaStorageKey(lessonId, userId), ileContentFingerprint(content));
    storage.setItem(
      ileProgressStorageKey(lessonId, userId),
      JSON.stringify([...completedBlockIds]),
    );
    storage.setItem(ileSectionStorageKey(lessonId, userId), JSON.stringify(sectionIndex));
    if (blockResults) {
      storage.setItem(
        ileResultsStorageKey(lessonId, userId),
        JSON.stringify(Object.fromEntries(blockResults)),
      );
    }
  } catch {
    // ignore quota / private mode
  }
}

export function loadIleSessionState(
  lessonId: string,
  content: LessonContent,
  maxSectionIndex: number,
  userId?: string | null,
): { completedBlockIds: Set<string>; sectionIndex: number; blockResults: Map<string, IleBlockResult> } {
  const storage = getStorage();
  if (!storage) {
    return { completedBlockIds: new Set(), sectionIndex: 0, blockResults: new Map() };
  }

  const fingerprint = ileContentFingerprint(content);
  const validBlockIds = new Set(content.sections.flatMap((s) => s.blocks.map((b) => b.id)));

  try {
    const storedFingerprint = storage.getItem(ileMetaStorageKey(lessonId, userId));
    if (storedFingerprint && storedFingerprint !== fingerprint) {
      clearIleSessionProgress(lessonId, userId);
      storage.setItem(ileMetaStorageKey(lessonId, userId), fingerprint);
      return { completedBlockIds: new Set(), sectionIndex: 0, blockResults: new Map() };
    }

    if (storage.getItem(`ile-progress:${lessonId}`)) {
      storage.removeItem(`ile-progress:${lessonId}`);
      storage.removeItem(`ile-section:${lessonId}`);
    }

    let completedBlockIds = new Set<string>();
    const progressRaw = storage.getItem(ileProgressStorageKey(lessonId, userId));
    if (progressRaw) {
      const parsed = JSON.parse(progressRaw) as unknown;
      if (Array.isArray(parsed)) {
        completedBlockIds = new Set(
          parsed.filter((id): id is string => typeof id === "string" && (validBlockIds.size === 0 || validBlockIds.has(id)))
        );
      }
    }

    let sectionIndex = 0;
    const sectionRaw = storage.getItem(ileSectionStorageKey(lessonId, userId));
    if (sectionRaw) {
      const parsed = JSON.parse(sectionRaw) as unknown;
      if (typeof parsed === "number" && Number.isInteger(parsed)) {
        sectionIndex = Math.min(Math.max(parsed, 0), maxSectionIndex);
      }
    }

    const blockResults = new Map<string, IleBlockResult>();
    const resultsRaw = storage.getItem(ileResultsStorageKey(lessonId, userId));
    if (resultsRaw) {
      const parsed = JSON.parse(resultsRaw) as unknown;
      if (parsed && typeof parsed === "object") {
        for (const [id, meta] of Object.entries(parsed as Record<string, unknown>)) {
          if (meta && typeof meta === "object") blockResults.set(id, meta as IleBlockResult);
        }
      }
    }

    storage.setItem(ileMetaStorageKey(lessonId, userId), fingerprint);
    return { completedBlockIds, sectionIndex, blockResults };
  } catch {
    return { completedBlockIds: new Set(), sectionIndex: 0, blockResults: new Map() };
  }
}

/** Load session state and retain progress across page reloads and tab navigation. */
export function resolveIleSessionState(
  lessonId: string,
  content: LessonContent,
  maxSectionIndex: number,
  lessonCompletedInCourse: boolean,
  userId?: string | null,
): { completedBlockIds: Set<string>; sectionIndex: number; blockResults: Map<string, IleBlockResult> } {
  const state = loadIleSessionState(lessonId, content, maxSectionIndex, userId);
  if (lessonCompletedInCourse) {
    const allInteractiveIds = new Set(content.sections.flatMap((s) => getInteractiveBlockIds(s)));
    return {
      completedBlockIds: new Set([...state.completedBlockIds, ...allInteractiveIds]),
      sectionIndex: 0,
      blockResults: state.blockResults,
    };
  }
  return state;
}
